import React, { useState } from "react";
import { FilterOutlined, CloseCircleOutlined } from "@ant-design/icons";

import DebounceInput from "../DebounceInput";

const FilterPanel = ({
  categories = [],
  onClearAll,
  onClose,
  title = "Filters",
  width = 520,
  footer = null,
  // Optional override for the "Clear all" enabled state. By default the action
  // is enabled whenever any category reports a count, but a caller whose counts
  // include a filter the user isn't allowed to remove (e.g. a permission-forced
  // default) can force it off.
  clearAllDisabled,
}) => {
  const [activeKey, setActiveKey] = useState(categories[0]?.key || "");
  const [search, setSearch] = useState("");

  const totalFilterCount = categories.reduce((sum, cat) => sum + (cat.count || 0), 0);
  const canClearAll = clearAllDisabled !== undefined ? !clearAllDisabled : totalFilterCount > 0;

  const activeCategory = categories.find(c => c.key === activeKey);
  const searchable = activeCategory?.searchable !== false;

  const handleCategoryChange = key => {
    setActiveKey(key);
    setSearch("");
  };

  return (
    <div style={{ width }} className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-solid border-0 border-b border-[#f0f0f0]">
        <div className="flex items-center gap-2">
          <FilterOutlined className="text-black-88" />
          <span className="text-black-88 font-semibold text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {onClearAll && (
            <span
              className={`text-sm font-normal ${
                canClearAll ? "text-twLinkColor cursor-pointer" : "text-grey-300 cursor-not-allowed"
              }`}
              onClick={canClearAll ? onClearAll : undefined}
            >
              Clear all
            </span>
          )}
          {onClose && (
            <CloseCircleOutlined
              className="text-[#00000073] cursor-pointer text-base"
              onClick={onClose}
            />
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex" style={{ height: 350 }}>
        {/* Sidebar */}
        <div
          className="flex flex-col border-solid border-0 border-r border-[#f0f0f0] py-1"
          style={{ minWidth: 170 }}
        >
          {categories.map(cat => {
            const isActive = cat.key === activeKey;
            return (
              <div
                key={cat.key}
                className={`px-4 py-2 flex items-center justify-between cursor-pointer text-sm font-normal transition-all duration-200 ${
                  isActive
                    ? "text-twLinkColor border border-solid border-y-0 border-r-0 border-l-2 border-l-twLinkColor"
                    : "text-black-88 border-l-2 border-y-0 border-r-0 border-l-transparent hover:bg-gray-50"
                }`}
                onClick={() => handleCategoryChange(cat.key)}
              >
                <span className="truncate">{cat.label}</span>
                {cat.count > 0 && (
                  <span className="text-twLinkColor ml-1 shrink-0">({cat.count})</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search bar */}
          {searchable && (
            <div className="p-3 border-solid border-0 border-b border-[#f0f0f0]">
              <DebounceInput
                placeholder={activeCategory?.searchPlaceholder || "Search..."}
                value={search}
                onChange={val => setSearch(val)}
                allowClear
                size="middle"
                debounceTime={300}
              />
            </div>
          )}
          {/* List */}
          <div key={activeKey} className="flex-1 overflow-y-auto animate-slideIn">
            {activeCategory?.render?.(search, activeCategory?.onClear)}
          </div>
        </div>
      </div>

      {/* Footer (optional) — hosts the Saved filters dropdown + Save filter
          action for Activity Center. Rendered only when a consumer supplies it
          so other FilterPanel users are unaffected. */}
      {footer && (
        <div className="px-4 py-2.5 border-solid border-0 border-t border-[#f0f0f0]">{footer}</div>
      )}
    </div>
  );
};

export default FilterPanel;
